import LegalPageLayout from './LegalPageLayout'

const content = `# Cookies & Similar Technologies Policy — EasyAppIcon API

**Last updated:** 12 Jan 2026

This Cookies & Similar Technologies Policy (the "**Policy**") explains how **EasyIO trading as "EasyAppIcon"** ("**EasyAppIcon**," "**we**," "**us**," "**our**") uses cookies and similar technologies on **api.easyappicon.com** and related pages (including our dashboard) (collectively, the "**Services**").

This Policy describes what these technologies are, why we use them, and the choices available to you.

---

## 1) Definitions

**Cookies** are small text files stored on your device when you interact with a website.

**Similar technologies** may include pixels/web beacons, tags, SDKs (where applicable), and browser storage such as local storage and session storage. In this Policy, we refer to cookies and similar technologies together as "**Cookies**" unless the context indicates otherwise.

Cookies may be:

* **First‑party Cookies** — set by EasyAppIcon; or
* **Third‑party Cookies** — set by our service providers (for example, analytics or security providers).

Cookies can involve identifiers (e.g., device or browser identifiers) that may be treated as personal data under applicable laws, if combined with other information.

---

## 2) How we use Cookies

We use Cookies for the purposes described below.

| Category                    | Purpose                                                                                                          | Mandatory? |
| --------------------------- | ---------------------------------------------------------------------------------------------------------------- | ---------- |
| **Strictly Necessary**      | Authentication, session management, security controls, fraud/abuse prevention, and basic site operation.         | **Yes**    |
| **Functional**              | Remembering preferences (e.g., language or interface settings).                                                  | Optional   |
| **Analytics / Performance** | Understanding and improving performance and usability (currently via **Google Analytics 4**; see Appendix A).    | Optional   |
| **Marketing / Attribution** | Measuring the effectiveness of growth campaigns and understanding how users arrive at the Services (if enabled). | Optional   |

---

## 3) Your choices & management

### A) Universal opt‑out signals (e.g., Global Privacy Control)

Some browsers and extensions allow you to send **opt‑out preference signals** (such as **Global Privacy Control (GPC)**). Where required by applicable law and where our systems can detect the signal, we will treat it as a request to opt out of certain non‑essential processing, such as selling/sharing of personal data or targeted advertising.

**Important:** GPC recognition applies only to the extent required by law and where we can detect the signal; it may not apply to all processing activities. If you have questions about what opt-outs are available, contact us using the details in Section 6.

### B) Re‑managing preferences / withdrawing consent

Where required by law, you can change your choices at any time by re-opening our consent manager.

If available on the Services, you can click **"Manage Cookie Settings"** in the footer to revisit your preferences. If that link is not available in your region, you can also control Cookies through your browser settings (see below).

### C) Browser controls

Most browsers allow you to delete or block Cookies. Note that if you block **Strictly Necessary** Cookies, you may not be able to log in to the EasyAppIcon API dashboard or use certain security-related features.

---

## 4) Pixels and web beacons

We may use small snippets of code (pixels or web beacons) to understand whether certain events occurred (for example, page events) and to troubleshoot reliability issues.

In addition, we may include a pixel in certain **system emails** (for example, to confirm delivery or help diagnose sending issues). These pixels do not install software on your device. Your email client may allow you to disable automatic image loading to limit this.

---

## 5) Interest‑based advertising

We do not currently share your data with third-party ad networks for the purpose of following you across the web.

If we introduce advertising partners in the future, we will provide notice and, where required by law, request your prior consent before enabling advertising Cookies.

---

## Appendix A — Google Analytics 4 (GA4)

If you visit our Services and you have consented to analytics Cookies where required, **Google Analytics 4** may set the following first-party cookies:

| Cookie name          | Default expiration | Purpose                                   |
| -------------------- | -----------------: | ----------------------------------------- |
| \`_ga\`                |            2 years | Distinguishes unique users for analytics. |
| \`_ga_<container-id>\` |            2 years | Persists session state for analytics.     |

**Note:** Some browsers may shorten cookie lifetimes (for example, Safari may reduce some cookie lifetimes to ~7 days) to protect user privacy.

---

## 6) Contact

Questions about this Policy or our use of Cookies?

**EasyIO trading as "EasyAppIcon"**
Email: [supports@easyappicon.com](mailto:supports@easyappicon.com)
Address: West Wing 2/F, 822 Lai Chi Kok Road, Cheung Sha Wan, Kowloon, Hong Kong
`

const CookiePolicy = () => {
  return (
    <LegalPageLayout
      title="Cookie Policy"
      lastUpdated="January 12, 2026"
      metaDescription="EasyAppIcon API Cookie Policy - Learn how we use cookies and similar technologies on our website and your choices for managing them."
      content={content}
    />
  )
}

export default CookiePolicy
